import { z } from "zod";

/**
 * Phase 1 permission set. Permission strings follow
 * `<resource>:<action>[:<sub-action>]`.
 *
 * Tenant scoping is *not* encoded in the permission name — the backend
 * `RequestContext` enforces tenant boundaries via repository guards
 * (`core-repository-base`) and only widens scope for users who hold
 * `PlatformAdmin`.
 *
 * Adding a permission requires updating the role->permission map in
 * `backend/src/shared/permissions.ts` and any callers that gate on it.
 */
export const Permission = z.enum([
  // Tenants
  "tenants:list",
  "tenants:read",
  "tenants:create",
  "tenants:update",
  "tenants:archive",

  // Users (within tenant)
  "users:list",
  "users:read",
  "users:invite",
  "users:update",
  "users:disable",
  "users:enable",
  "users:password:reset",
  "users:roles:read",
  "users:roles:assign",
  "users:roles:revoke",
  "users:features:read",
  "users:features:update",

  // Sites / facility hierarchy
  "sites:list",
  "sites:read",
  "sites:create",
  "sites:update",
  "sites:delete",
  "buildings:read",
  "buildings:create",
  "buildings:update",
  "buildings:delete",
  "floors:read",
  "floors:create",
  "floors:update",
  "floors:delete",

  // Assets
  "assets:list",
  "assets:read",
  "assets:create",
  "assets:update",
  "assets:delete",

  // Devices
  "devices:list",
  "devices:read",
  "devices:create",
  "devices:update",
  "devices:delete",
  "devices:assign",
  "devices:unassign",

  // Alerts
  "alerts:list",
  "alerts:read",
  "alerts:create",
  "alerts:acknowledge",
  "alerts:dismiss",
  "alerts:resolve",
  "alerts:link",
  "alerts:link-work-item",

  // Work items
  "work-items:list",
  "work-items:read",
  "work-items:create",
  "work-items:update",
  "work-items:move",
  "work-items:assign",
  "work-items:close",

  // Commands (ledger-only until simulator dispatch lands)
  "commands:list",
  "commands:read",
  "commands:create",
  "commands:execute",

  // Devices — network identity
  "devices:network-identity:read",
  "devices:network-identity:write",

  // Integrations (health/admin foundations)
  "integrations:list",
  "integrations:read",
  "integrations:create",
  "integrations:update",
  "integrations:manage",
  "integrations:admin",
  "integrations:disable",
  "integrations:force-refresh",
  "integrations:create-mapping",
  "integrations:approve-mapping",
  "integrations:reject-mapping",

  // Adapter registry
  "adapters:read",
  "adapters:manage",

  // Audit
  "audit:read",

  // Platform-wide
  "platform:admin",

  // ── End-user / field-app permissions ────────────────────────────────────
  // Field scope assignments (admin manages, field users read)
  "field-scope-assignments:read",
  "field-scope-assignments:manage",

  // Service provider organizations
  "service-provider-orgs:read",
  "service-provider-orgs:manage",

  // FieldWorker permissions (scoped to assigned work/assets)
  "mobile:work:read:assigned",
  "mobile:work:read:scope",
  "mobile:work:create:scope",
  "mobile:work:update:assigned",
  "mobile:work:createFollowUp:assigned",
  "mobile:work:markReadyForReview:assigned",
  "mobile:asset:read:scope",
  "mobile:device:read:scope",
  "mobile:alert:read:related",
  "mobile:history:read:related",
  "mobile:maintenance:confirm:assigned",
  "mobile:command:refreshStatus:scope",
  "mobile:command:requestTelemetry:scope",
  "mobile:command:runDiagnostic:scope",

  // FieldManager additional permissions
  "mobile:field:read:scope",
  "mobile:field:assign:scope",
  "mobile:field:review:scope",
  "mobile:work:reassign:scope",
  "mobile:maintenance:review:scope",

  // ── Portal permissions ───────────────────────────────────────────────────
  "portal:assets:read",
  "portal:alerts:read",
  "portal:alerts:acknowledge",
  "portal:reports:read",
]);
export type Permission = z.infer<typeof Permission>;

export const ALL_PERMISSIONS: readonly Permission[] = Permission.options;

/**
 * Response of `GET /me/permissions`. The list is the *resolved* permission
 * set after combining role assignments and feature entitlements; UIs should
 * gate on this, not on raw role names.
 */
export const PermissionsResponse = z.object({
  permissions: z.array(Permission),
});
export type PermissionsResponse = z.infer<typeof PermissionsResponse>;
